'use client';

import { PublicKey } from '@velocity-exchange/sdk';
import {
	EmptyRow,
	EMPTY_ORDERBOOK_ROW,
	OrderbookGrouping,
	CUMULATIVE_SIZE_CURRENCY,
	OrderBookBidAsk,
	OrderBookDisplayStateBidAsk,
	mergeBidsAndAsksForGroupsize,
	getZeroPaddingForGroupingSize,
	getBucketForUserLiquidity,
	getBucketFloorForPrice,
	GroupingSizeQuoteValue,
} from '@velocity-exchange/common';
import React, {
	PropsWithChildren,
	RefCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { cj, cm } from 'src/utils/classNames';
import useDevSwitchIsOn from '../../hooks/useDevSwitchIsOn';
import DevOrderbookPanel from '../Dev/DevOrderbookPanel';
import InlineLoadingBar from '../InlineLoadingBar/InlineLoadingBar';
import useOrderbookDisplayStore from '../../stores/orderbook/useOrderbookDisplayStore';
import {
	ORDERBOOK_DISPLAY_TYPE,
	useOrderbookDisplayPreference,
} from './useOrderbookDisplayPreference';
import { shouldBeFlexReversed, shouldBeArrayReversed } from './OrderbookUtils';
import {
	OrderBookDisplayState,
	V2OrderbookDisplayProps,
} from './OrderbookTypes';
import usePostHogCapture from '../../hooks/posthog/usePostHogCapture';
import useCurrentAuthority from '../../hooks/useCurrentAuthority';
import useDevStore from '../../stores/useDevStore';
import useInfoForCurrentlySelectedMarket from 'src/hooks/useInfoForCurrentlySelectedMarket';
import { useMarketStepSize } from 'src/hooks/useMarketStepSize';
import useIsTabletOrSmallerSize from '../../hooks/useIsTabletOrSmallerSize';
import { EmptyOrderbookRow, OrderbookRow } from './OrderbookRow';
import { OrderbookDivider } from './OrderbookDivider';
import OrderbookTableHeader from './OrderbookTableHeader';
import { useOrderbookRowHoverStateDisplayProps } from './hooks/useOrderbookRowHoverStateDisplayProps';
import {
	MAX_ORDERBOOK_DEPTH,
	ORDERBOOK_ROW_GAP_DESKTOP,
	ORDERBOOK_ROW_GAP_MOBILE,
} from './OrderbookConstants';
import { notify } from 'src/utils/notifications';
import { dlog } from 'src/dev';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';
import OrderbookFooter from 'src/components/Orderbook/OrderbookFooter';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

const ALERT_CROSSING_OB =
	process?.env?.NEXT_PUBLIC_ALERT_CROSSING_OB === 'true';

const getCrossingObDebugInfo = (
	authority: PublicKey,
	processedOrderbookState: OrderBookDisplayState,
	orderbookProps: V2OrderbookDisplayProps
) => {
	const top3ProcessedBidsAndAsks = {
		bids: processedOrderbookState.bids.slice(0, 3),
		asks: processedOrderbookState.asks.slice(0, 3),
	};

	const top3PropsBidsAndAsks = {
		bids: orderbookProps.orderbookDisplayState.bids.slice(0, 3),
		asks: orderbookProps.orderbookDisplayState.asks.slice(0, 3),
	};

	return {
		authority: authority?.toString(),
		top3ProcessedBidsAndAsks,
		top3PropsBidsAndAsks,
		depth: orderbookProps.fixedVerticalSideDepth,
		groupingSizeSelection: orderbookProps.groupingSizeSelection,
		currentUserBidsAndAsks: orderbookProps.currentUserBidsAndAsks,
	};
};

/*
 * We use a relative wrapper + absolute container so that the height of the orderbook rows don't affect the dragging resizer motion.
 * If we didn't, the draggable resizer will get "stuck" until the extreme orderbook rows can be hidden.
 */
const OrderbookContainerWrapper = (props: {
	children: React.ReactNode;
	displayType: ORDERBOOK_DISPLAY_TYPE;
	ref: RefCallback<HTMLDivElement>;
}) => {
	return (
		<div
			className={cj(
				// `overflow-hidden` is necessary to ensure rows don't overflow during quick resizing.
				'relative grow overflow-hidden',
				props.displayType === ORDERBOOK_DISPLAY_TYPE.HORIZONTAL &&
					'hidden-scroll flex size-full overflow-auto'
			)}
			ref={props.ref}
		>
			{props.children}
		</div>
	);
};

const OrderbookSectionsContainer = (props: {
	children: React.ReactNode;
	displayType: ORDERBOOK_DISPLAY_TYPE;
	isLoading: boolean;
}) => {
	if (props.isLoading) {
		return (
			<div className="flex h-full w-full items-center justify-center">
				<InlineLoadingBar />
			</div>
		);
	}

	return props.displayType === ORDERBOOK_DISPLAY_TYPE.HORIZONTAL ? (
		props.children
	) : (
		<div className="absolute inset-0 box-border flex h-full max-h-full w-full grow flex-col-reverse">
			{props.children}
		</div>
	);
};

const OrderbookRowsWrapper = (
	props: PropsWithChildren<{
		displayType: ORDERBOOK_DISPLAY_TYPE;
		side: 'buy' | 'sell';
		ref: React.RefObject<HTMLDivElement>;
	}>
) => {
	const flexReversed = shouldBeFlexReversed(props.side, props.displayType);
	const isVertical = props.displayType === ORDERBOOK_DISPLAY_TYPE.VERTICAL;
	const isMobileScreenSize = useIsMobileScreenSize();

	const style = useMemo(() => {
		return {
			rowGap: `${
				isMobileScreenSize
					? ORDERBOOK_ROW_GAP_MOBILE
					: ORDERBOOK_ROW_GAP_DESKTOP
			}px`,
		};
	}, [isMobileScreenSize]);

	return (
		<div
			ref={props.ref}
			data-puppet-tag={props.side}
			className={cm(
				'flex flex-1 flex-col justify-center',
				!isVertical && flexReversed && 'justify-end',
				flexReversed && 'flex-col-reverse',
				!isVertical ? 'w-1/2' : 'w-full'
			)}
			style={style}
		>
			{props.children}
		</div>
	);
};

const getEmptyFilledBidsAsks = (
	bidsOrAsks: OrderBookDisplayStateBidAsk[],
	depth: number = MAX_ORDERBOOK_DEPTH
): (OrderBookDisplayStateBidAsk | EmptyRow)[] => {
	const result = Array(depth).fill(EMPTY_ORDERBOOK_ROW);

	bidsOrAsks.forEach((bidOrAsk, index) => {
		if (index < depth) {
			result[index] = bidOrAsk;
		}
	});

	return result;
};

const OrderbookDisplay = (props: V2OrderbookDisplayProps) => {
	const marketInfo = useInfoForCurrentlySelectedMarket();
	const uncrossDisabled = useDevStore((s) => s.dlobSettings.disableUncross);
	const posthogCapture = usePostHogCapture();

	const isEmulatingAccount = useDriftStore((s) => s.isEmulatingAccount);
	const authority = useCurrentAuthority();

	const { orderbookDisplayType } = useOrderbookDisplayPreference({
		isMini: props.isMini,
	});

	const orderbookContainerRef = useRef<HTMLDivElement>(null);
	const buySideWrapperRef = useRef<HTMLDivElement>(null);
	const sellSideWrapperRef = useRef<HTMLDivElement>(null);

	const isMobile = useIsTabletOrSmallerSize();

	const groupingSizeValue = props.groupingSizeSelection;

	const groupingSizeQuoteValue = useMemo((): GroupingSizeQuoteValue => {
		const foundOption = props.groupingSizeOptions.find(
			(option) => option.groupingSize === groupingSizeValue
		);

		// If no matching option is found, use the first option's quoteVal as fallback, or 0 if no options exist
		const quoteVal =
			foundOption?.quoteVal ?? props.groupingSizeOptions[0]?.quoteVal ?? 0;

		// Safe creation of GroupingSizeQuoteValue from number (opaque type requires intermediate unknown)
		return quoteVal as unknown as GroupingSizeQuoteValue;
	}, [props.groupingSizeOptions, groupingSizeValue]);

	const [cumulativeSizeCurrency, setCumulativeSizeCurrency] =
		useState<CUMULATIVE_SIZE_CURRENCY>(CUMULATIVE_SIZE_CURRENCY.BASE);
	const updateGroupingSelection = props.updateGroupingSizeSelection;

	const zeroPadding = getZeroPaddingForGroupingSize(groupingSizeQuoteValue);

	const orderBookState = props.orderbookDisplayState;

	// Base decimals displayed should be number of decimals in min step size
	const stepSize = useMarketStepSize(marketInfo?.info?.marketId);
	const baseDisplayDecimals = (`${stepSize}`.split('.')[1] ?? '').length;

	const onGroupSizeChange = (groupSize: number) => {
		updateGroupingSelection(groupSize as OrderbookGrouping);
	};

	const devSwitchIsOn = useDevSwitchIsOn();

	const isLoading =
		props.loading ||
		(!orderBookState.bids?.length && !devSwitchIsOn) ||
		!props.marketPriceState;

	const finalDepth =
		orderbookDisplayType === ORDERBOOK_DISPLAY_TYPE.VERTICAL
			? (props.fixedVerticalSideDepth ??
				Math.max(orderBookState.bids.length, orderBookState.asks.length))
			: (props.fixedHorizontalSideDepth ??
				Math.max(orderBookState.bids.length, orderBookState.asks.length));

	// # User Bid Lookups - Used to signal the user's orders on the book
	const userBidPriceBucketLookup = useMemo(() => {
		const mergedUserBids = mergeBidsAndAsksForGroupsize(
			'bid',
			props.currentUserBidsAndAsks.bids,
			groupingSizeQuoteValue
		);

		const bucketLookup = new Map<number, OrderBookBidAsk>(
			mergedUserBids.map((bid) => {
				const priceBucket = getBucketForUserLiquidity(
					bid.price,
					groupingSizeQuoteValue,
					'bid'
				);
				return [priceBucket, bid];
			})
		);

		return bucketLookup;
	}, [props.currentUserBidsAndAsks.bids, groupingSizeQuoteValue]);

	const userAskPriceBucketLookup = useMemo(() => {
		const mergedUserAsks = mergeBidsAndAsksForGroupsize(
			'ask',
			props.currentUserBidsAndAsks.asks,
			groupingSizeQuoteValue
		);

		const bucketLookup = new Map<number, OrderBookBidAsk>(
			mergedUserAsks.map((ask) => {
				const priceBucket = getBucketForUserLiquidity(
					ask.price,
					groupingSizeQuoteValue,
					'ask'
				);
				return [priceBucket, ask];
			})
		);

		return bucketLookup;
	}, [props.currentUserBidsAndAsks.asks, groupingSizeQuoteValue]);

	const getUserLiquidityForPrice = (price: number, side: 'bid' | 'ask') => {
		let isCurrentUserLiquidity = false;
		let currentUserLiquiditySize = 0;

		const priceToUse = price;
		const priceBucket = getBucketFloorForPrice(
			priceToUse,
			groupingSizeQuoteValue
		);

		const lookupBucket =
			side === 'bid' ? userBidPriceBucketLookup : userAskPriceBucketLookup;

		const matchingUserBidAsk = lookupBucket.get(priceBucket);

		if (matchingUserBidAsk) {
			isCurrentUserLiquidity = true;
			currentUserLiquiditySize = matchingUserBidAsk.size;
		}

		return { isCurrentUserLiquidity, currentUserLiquiditySize };
	};

	// Hook to alert with orderbook crossing warnings
	useEffect(() => {
		if (uncrossDisabled) return;
		if (!devSwitchIsOn) return;
		if (orderBookState.bids.length === 0) return;
		if (orderBookState.asks.length === 0) return;
		if (isEmulatingAccount) return; // Skip logging crossing toasts when emulating. Because we expect the orderbook to cross when looking at a market maker which is likely to be the case when emulating

		if (orderBookState.bids[0].price > orderBookState.asks[0].price) {
			if (ALERT_CROSSING_OB) {
				notify({
					type: 'warning',
					message: 'Orderbook is crossed',
					description: 'Check dev logs in console for debugging',
				});
			}

			dlog(`crossed_orderbook`, `crossed_orderbook_state`, {
				orderBookState,
				props,
			});

			const debugInfo = getCrossingObDebugInfo(
				authority ?? PublicKey.default,
				orderBookState,
				props
			);

			posthogCapture.captureEvent('trade_orderbook_crossed', {
				debugInfo,
			});
		}
	}, [
		orderBookState.bids,
		orderBookState.asks,
		authority,
		uncrossDisabled,
		isEmulatingAccount,
	]);

	const orderbookRowHoverStateDisplayProps =
		useOrderbookRowHoverStateDisplayProps(
			orderBookState.bids.length,
			orderBookState.asks.length,
			orderbookContainerRef as React.RefObject<HTMLDivElement>,
			buySideWrapperRef as React.RefObject<HTMLDivElement>,
			sellSideWrapperRef as React.RefObject<HTMLDivElement>
		);

	const bidsToRender = useMemo(() => {
		const emptyFilledBids = getEmptyFilledBidsAsks(
			orderBookState.bids,
			finalDepth
		);

		const bids = shouldBeArrayReversed('buy', orderbookDisplayType)
			? emptyFilledBids.toReversed()
			: emptyFilledBids;

		return bids;
	}, [orderBookState.bids, orderbookDisplayType, finalDepth]);

	const asksToRender = useMemo(() => {
		const emptyFilledAsks = getEmptyFilledBidsAsks(
			orderBookState.asks,
			finalDepth
		);

		const asks = shouldBeArrayReversed('sell', orderbookDisplayType)
			? emptyFilledAsks.toReversed()
			: emptyFilledAsks;

		return asks;
	}, [orderBookState.asks, orderbookDisplayType, finalDepth]);

	const getMarketTickSize = useGetTickSizeForMarket();
	const tickSize = getMarketTickSize(props.marketId);
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);

	const headerGroupingSizeOptions = useMemo(() => {
		return props.groupingSizeOptions.map((option) => ({
			value: option.groupingSize,
			label: option.quoteVal.toString(),
		}));
	}, [props.groupingSizeOptions]);

	const waitingForNewGroupingData = useOrderbookDisplayStore(
		(s) => s.waitingForNewGroupingData
	);

	return (
		<div
			className={cj(
				'relative max-h-full grow overflow-hidden bg-container-bg transition-opacity duration-100',
				'sm:rounded-sm',
				waitingForNewGroupingData && 'opacity-60'
			)}
			ref={orderbookContainerRef}
			data-testid={props.dataTestId}
		>
			{
				<div className="flex h-full flex-col bg-container-bg text-text-default xs:min-h-[25vh] md:min-h-0">
					<>
						<OrderbookTableHeader
							displayType={orderbookDisplayType}
							marketSymbol={
								marketInfo?.info?.genericInfo?.baseAssetSymbol ?? ''
							}
							groupingSizeOptions={headerGroupingSizeOptions}
							onGroupSizeChange={onGroupSizeChange}
							groupingSelection={groupingSizeValue}
							cumulativeSizeCurrency={cumulativeSizeCurrency}
							onCurrencyChange={setCumulativeSizeCurrency}
							isLoading={isLoading}
							isMini={props.isMini}
						/>
						{!isLoading &&
							orderbookDisplayType === ORDERBOOK_DISPLAY_TYPE.HORIZONTAL && (
								<OrderbookDivider
									midPrice={props.marketPriceState?.markPrice}
									spread={{
										quote: props.marketPriceState?.spreadQuote,
										quotePct: props.marketPriceState?.spreadPct,
									}}
									tickSizeDecimalPlaces={tickSizeDecimalPlaces}
								/>
							)}

						<OrderbookContainerWrapper
							displayType={orderbookDisplayType}
							ref={orderBookState.containerCallbackRef}
						>
							<OrderbookSectionsContainer
								displayType={orderbookDisplayType}
								isLoading={isLoading}
							>
								<OrderbookRowsWrapper
									side="buy"
									displayType={orderbookDisplayType}
									ref={buySideWrapperRef as React.RefObject<HTMLDivElement>}
								>
									{bidsToRender.map((_bid, index) => {
										if (_bid === EMPTY_ORDERBOOK_ROW) {
											return <EmptyOrderbookRow key={`bid_${index}`} />;
										}

										const bid = _bid as OrderBookDisplayStateBidAsk;

										const { isCurrentUserLiquidity, currentUserLiquiditySize } =
											getUserLiquidityForPrice(bid.price, 'bid');

										const {
											isCurrentUserLiquidity: isCurrentUserLiquidityAsk,
										} = getUserLiquidityForPrice(bid.price, 'ask');

										return (
											<OrderbookRow
												/**
												 * Using the index for key is usually not good, but in this specific type of usage,
												 * the tooltip requires the key to stay consistent based on the position in the table,
												 * otherwise it behaves incorrectly
												 */
												key={`bid_${index}`}
												price={bid.price}
												rowSize={bid.size}
												cumulativeSize={bid.cumulativeSize}
												cumulativeAvgPrice={bid.cumulativeAvgPrice}
												totalSize={orderBookState.bidTotalSize}
												side="buy"
												zeroPadding={zeroPadding}
												isCurrentUserLiquidity={
													isCurrentUserLiquidity || isCurrentUserLiquidityAsk
												}
												currentUserLiquiditySize={currentUserLiquiditySize}
												displayType={orderbookDisplayType}
												baseDisplayDecimals={baseDisplayDecimals}
												isMobile={isMobile}
												otherSideWrapperBoundingClientRect={
													orderbookRowHoverStateDisplayProps.sellSideWrapperBoundingClientRect
												}
												rowsSectionWrapperBoundingClientRect={
													orderbookRowHoverStateDisplayProps.rowsSectionWrapperBoundingClientRect
												}
												cumulativeSizeCurrency={cumulativeSizeCurrency}
												isMini={props.isMini}
											/>
										);
									})}
								</OrderbookRowsWrapper>

								{orderbookDisplayType === ORDERBOOK_DISPLAY_TYPE.VERTICAL && (
									<OrderbookDivider
										midPrice={props.marketPriceState?.markPrice}
										spread={{
											quote: props.marketPriceState?.spreadQuote,
											quotePct: props.marketPriceState?.spreadPct,
										}}
										isMini={props.isMini}
										tickSizeDecimalPlaces={tickSizeDecimalPlaces}
									/>
								)}

								<OrderbookRowsWrapper
									side="sell"
									displayType={orderbookDisplayType}
									ref={sellSideWrapperRef as React.RefObject<HTMLDivElement>}
								>
									{asksToRender.map((_ask, index) => {
										if (_ask === EMPTY_ORDERBOOK_ROW) {
											return <EmptyOrderbookRow key={`ask_${index}`} />;
										}

										const ask = _ask as OrderBookDisplayStateBidAsk;

										const { isCurrentUserLiquidity, currentUserLiquiditySize } =
											getUserLiquidityForPrice(ask.price, 'ask');
										const {
											isCurrentUserLiquidity: isCurrentUserLiquidityBid,
										} = getUserLiquidityForPrice(ask.price, 'bid');

										return (
											<OrderbookRow
												/**
												 * Using the index for key is usually not good, but in this specific type of usage,
												 * the tooltip requires the key to stay consistent based on the position in the table,
												 * otherwise it behaves incorrectly
												 */
												key={`ask_${index}`}
												price={ask.price}
												rowSize={ask.size}
												cumulativeSize={ask.cumulativeSize}
												totalSize={orderBookState.askTotalSize}
												cumulativeAvgPrice={ask.cumulativeAvgPrice}
												side="sell"
												zeroPadding={zeroPadding}
												isCurrentUserLiquidity={
													isCurrentUserLiquidity || isCurrentUserLiquidityBid
												}
												currentUserLiquiditySize={currentUserLiquiditySize}
												displayType={orderbookDisplayType}
												baseDisplayDecimals={baseDisplayDecimals}
												isMobile={isMobile}
												otherSideWrapperBoundingClientRect={
													orderbookRowHoverStateDisplayProps.buySideWrapperBoundingClientRect
												}
												rowsSectionWrapperBoundingClientRect={
													orderbookRowHoverStateDisplayProps.rowsSectionWrapperBoundingClientRect
												}
												cumulativeSizeCurrency={cumulativeSizeCurrency}
												isMini={props.isMini}
											/>
										);
									})}
								</OrderbookRowsWrapper>
							</OrderbookSectionsContainer>
						</OrderbookContainerWrapper>
						{devSwitchIsOn && <DevOrderbookPanel {...props} />}
						<OrderbookFooter
							isMini={props.isMini}
							isLoading={isLoading}
							spreadQuote={props.marketPriceState?.spreadQuote}
							spreadPct={props.marketPriceState?.spreadPct}
							baseAssetSymbol={marketInfo?.info?.genericInfo?.baseAssetSymbol}
							tickSizeDecimalPlaces={tickSizeDecimalPlaces}
						/>
					</>
				</div>
			}
		</div>
	);
};

const OrderBookDisplayMemo = React.memo(OrderbookDisplay);

export default OrderBookDisplayMemo;
